import { useState } from "react";
import {
  Backdrop,
  Button,
  Divider,
  FormFieldSet,
  KeyValue,
  LogoLoader,
  Modal,
  NewTableAdv,
  PageTitle,
  showToast,
  StatusTags,
} from "../../../components";
import { useLoginUser } from "../../../hooks/useLoginUser";
import {
  dateFormator,
  fetchWFNotifications,
  formatWords,
  getValueByKey,
  mapTableRows,
} from "../../../utils";
import { useSelector } from "react-redux";
import { usePagination } from "../../../hooks";
import { useEffect } from "react";
import { formatCurrency } from "../../../utils/stringUtils";
import { JobStatus } from "../../../assets/data/enumData";
import axiosRequest from "../../../redux/slices/universal/axiosService";
import { ENDPOINTS } from "../../../redux/apiConfig";

const PlacementAlerts = ({ totalCount }) => {
  console.log({ totalCount });

  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;
  const [notificationData, setNotificationData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentModalData, setCurrentModalData] = useState({
    currentPlacement: {},
    selectedPlacement: {},
    employeePlacements: [],
  });
  console.log({ currentModalData });
  const notificationStore = useSelector((state) => state.wfNotification);

  const { page, limit, setPage, handleLimitChange } = usePagination(25);

  const colDetails = [
    {
      key: "title",
      label: "Title",
    },
    {
      key: "message",
      label: "Message",
    },
    {
      key: "placement",
      label: "Placement",
    },
    {
      key: "candidateName",
      label: "Candidate Name",
    },
    {
      key: "companyName",
      label: "Company Name",
    },
    {
      key: "startDate",
      label: "Start Date",
    },
    {
      key: "endDate",
      label: "End Date",
    },
    {
      key: "placementStatus",
      label: "Status",
      render: (row) => {
        return (
          <StatusTags
            tag={
              JobStatus?.find((status) => status.value === row.placementStatus)
                ?.label || "-"
            }
          />
        );
      },
    },
    {
      key: "action",
      label: "Action",
      render: (row) => {
        return (
          <>
            <Button
              type="button"
              title="Switch"
              variant="outlined"
              onClick={() => switchButtonHandler(row)}
              color="primary"
              size="sm"
            />
          </>
        );
      },
    },
  ];

  const switchButtonHandler = (row) => {
    if (row?.placement === "P1") {
      setCurrentModalData({
        currentPlacement: row,
        selectedPlacement: {},
        employeePlacements: [],
      });
      setShowModal(true);
    } else {
      showToast("Switch Already Completed.", "info", 5000);
      return;
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await fetchWFNotifications({
        limit: limit,
        page: page,
        filters: {
          userId: { type: "NONE", value: loginUserUiid },
          "meta.entity": { type: "NONE", value: "Placement" },
          event: { type: "NONE", value: "server.placement_closing_alert" },
        },
        sort: JSON.stringify({ updatedAt: -1 }),
        ref: "PlacementWithEmp",
        incl: ["uiid employeeId jobInfo clientPlacement payments"],
      });
      setNotificationData(
        res?.notifications?.map((row) => ({
          id: row?.meta?.id?.uiid,
          title: row?.title || "-",
          message: row?.message || "-",
          candidateName:
            (row?.meta?.id?.employeeId?.firstName || "") +
            " " +
            (row?.meta?.id?.employeeId?.lastName || ""),
          placement: row?.meta?.id?.clientPlacement?.placement || "-",
          companyName: row?.meta?.id?.jobInfo.billClient?.businessName || "-",
          startDate: dateFormator(row?.meta?.id?.jobInfo?.startDate),
          endDate: dateFormator(row?.meta?.id?.jobInfo?.endDate),
          placementStatus: row?.meta?.id?.jobInfo?.status,
          ...row,
        })) || [],
      );
    } catch (error) {
      console.error("error in fetchData TimesheetNotification", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [loginUserUiid, notificationStore, page, limit]);

  useEffect(() => {
    const fetchModalDropDown = async () => {
      if (showModal) {
        const placements = await axiosRequest({
          method: "GET",
          url: ENDPOINTS.PLACEMENTS.GET_ALL,
          params: {
            coll: "Placement",
            ref: "Client",
            incl: ["businessName"],
            fil: JSON.stringify({
              employeeId: {
                type: "NONE",
                value:
                  currentModalData?.currentPlacement?.meta?.id?.employeeId
                    ?.uiid,
              },
              "jobInfo.status": {
                type: "NONE",
                value: 1,
              },
              "clientPlacement.placement": {
                type: "EXCLUDE",
                value: "P1",
              },
            }),
            ret: "uiid jobInfo payments employeeId clientPlacement",
          },
        });
        setCurrentModalData((prev) => ({
          ...prev,
          employeePlacements:
            placements?.data?.data?.map((placement) => {
              return {
                label:
                  `${placement?.clientPlacement?.placement} - ${placement?.jobInfo?.billClient?.businessName}` ||
                  "-",
                value: placement?.uiid || "-",
                metaData: placement,
              };
            }) || [],
        }));
      }
    };
    fetchModalDropDown();
  }, [showModal, currentModalData?.currentPlacement?.id]);

  const handleConfirmSwitch = async () => {
    let swap1Completed = false;
    let swap2Completed = false;

    const selectedPlacementTag = {
      uiid: currentModalData.selectedPlacement.value,
      tag: currentModalData.selectedPlacement.metaData.clientPlacement
        .placement,
    };

    const currentPlacement = {
      uiid: currentModalData.currentPlacement.meta.id.uiid,
      tag: currentModalData.currentPlacement.meta.id.clientPlacement.placement,
    };

    const rollbackSwap1 = async () => {
      try {
        await axiosRequest({
          method: "PATCH",
          url: ENDPOINTS.PLACEMENTS.UPDATE,
          params: {
            coll: "Placement",
            uiid: currentPlacement.uiid,
          },
          data: {
            payload: [
              {
                type: "SET",
                value: {
                  "clientPlacement.placement": currentPlacement.tag,
                },
              },
            ],
          },
        });
      } catch (err) {
        console.error("ROLLBACK FAILED: swap1", err);
      }
    };

    const rollbackSwap2 = async () => {
      try {
        await axiosRequest({
          method: "PATCH",
          url: ENDPOINTS.PLACEMENTS.UPDATE,
          params: {
            coll: "Placement",
            uiid: selectedPlacementTag.uiid,
          },
          data: {
            payload: [
              {
                type: "SET",
                value: {
                  "clientPlacement.placement": selectedPlacementTag.tag,
                },
              },
            ],
          },
        });
      } catch (err) {
        console.error("ROLLBACK FAILED: swap2", err);
      }
    };

    try {
      if (!currentModalData?.selectedPlacement?.value) {
        showToast("No Placement Selected", "warning");
        return;
      }

      if (!currentModalData?.selectedPlacement?.metaData?.employeeId) {
        throw new Error("Missing employeeId in selected placement");
      }

      const payConfigRes = await axiosRequest({
        method: "GET",
        url: ENDPOINTS.EMPLOYEE_PAY_CONFIGURATION.GET_BY_UIID,
        params: {
          coll: "EmpPayConfig",
          fil: JSON.stringify({
            employeeId: {
              type: "NONE",
              value: currentModalData.selectedPlacement.metaData.employeeId,
            },
          }),
          ret: "uiid",
        },
      });

      const empPayConfigUiid = payConfigRes?.data?.data?.uiid;
      if (!empPayConfigUiid) {
        showToast("No Pay Configuration Found", "error");
        return;
      }

      const swap1 = await axiosRequest({
        method: "PATCH",
        url: ENDPOINTS.PLACEMENTS.UPDATE,
        params: {
          coll: "Placement",
          uiid: currentPlacement.uiid,
        },
        data: {
          payload: [
            {
              type: "SET",
              value: {
                "clientPlacement.placement": "-",
              },
            },
          ],
        },
      });

      if (!swap1?.success) throw new Error("Swap1 failed");
      swap1Completed = true;

      const swap2 = await axiosRequest({
        method: "PATCH",
        url: ENDPOINTS.PLACEMENTS.UPDATE,
        params: {
          coll: "Placement",
          uiid: selectedPlacementTag.uiid,
        },
        data: {
          payload: [
            {
              type: "SET",
              value: {
                "clientPlacement.placement": currentPlacement.tag,
              },
            },
          ],
        },
      });

      if (!swap2?.success) throw new Error("Swap2 failed");
      swap2Completed = true;

      const updateRes = await axiosRequest({
        method: "PATCH",
        url: ENDPOINTS.EMPLOYEE_PAY_CONFIGURATION.UPDATE,
        params: {
          coll: "EmpPayConfig",
          uiid: empPayConfigUiid,
        },
        data: {
          payload: [
            {
              type: "SET",
              value: {
                jobId: selectedPlacementTag.uiid,
              },
            },
          ],
        },
      });

      if (!updateRes?.success) throw new Error("EmpPayConfig update failed");

      fetchData();
      showToast("Assigned Successfully", "success");
      setCurrentModalData({
        currentPlacement: {},
        selectedPlacement: {},
        employeePlacements: [],
      });
      setShowModal(false);
    } catch (error) {
      console.error(
        "Transaction failed in handleConfirmSwitch, for Placements Alerts Switch Company",
        error,
      );

      // Rollback
      if (swap2Completed) await rollbackSwap2();
      if (swap1Completed) await rollbackSwap1();

      showToast("Placement Switch Failed, please try again", "error", 5000);
    }
  };

  const mappedrows = mapTableRows(notificationData, colDetails);

  return (
    <>
      <div className="px-3">
        <NewTableAdv
          // showCheckbox
          columns={colDetails}
          rows={mappedrows}
          serverPagination={true}
          totalCount={totalCount}
          limit={limit}
          page={page}
          onPageChange={setPage}
          onLimitChange={handleLimitChange}
          tdClassName="!py-4"
          toolbarRight={{
            search: true,
            add: [
              {
                component: (
                  <div className="flex items-center px-2">
                    <Divider orientation="vertical" />
                    <Button
                      type="icon"
                      icon={"refresh"}
                      color="black"
                      variant="text"
                    />
                  </div>
                ),
              },
            ],
          }}
        />
      </div>
      <Modal
        isOpen={showModal}
        closeOnOutsideClick
        onClose={() => {
          setCurrentModalData({
            currentPlacement: {},
            selectedPlacement: {},
            employeePlacements: [],
          });
          setShowModal(false);
        }}
        title="Switch Company"
        width="max-w-2xl"
      >
        <div className="flex flex-col gap-3 p-5">
          <div className=" bg-primary-100/40 p-6 rounded-md flex flex-col gap-2">
            <PageTitle title="Current Company" tag="h6" />
            <div className="flex justify-between items-center gap-2">
              <div className="w-10 h-10 leading-none text-sm aspect-square rounded-full flex items-center justify-center bg-primary/10 text-primary border border-primary">
                {currentModalData?.currentPlacement?.meta?.id?.clientPlacement
                  ?.placement || "-"}
              </div>
              <KeyValue
                label={currentModalData?.currentPlacement?.companyName || "-"}
                value={
                  currentModalData?.currentPlacement?.meta?.id?.jobInfo
                    ?.jobTitle || "-"
                }
                oneLine={false}
              />
              <KeyValue
                label={"Bill Rate"}
                value={formatCurrency(
                  currentModalData?.currentPlacement?.meta?.id?.payments[
                    currentModalData?.currentPlacement?.meta?.id?.payments
                      ?.length - 1
                  ]?.billRate?.rate || 0,
                )}
                oneLine={false}
              />
              <KeyValue
                label={"Pay Rate"}
                value={formatCurrency(
                  currentModalData?.currentPlacement?.meta?.id?.payments[
                    currentModalData?.currentPlacement?.meta?.id?.payments
                      ?.length - 1
                  ]?.payRate?.payRatePerHour || 0,
                )}
                oneLine={false}
              />
            </div>
          </div>
          <div className="flex items-center justify-center">
            <span className="material-symbols-outlined">arrow_cool_down</span>
          </div>
          <div className=" bg-primary-100/40 p-6 rounded-md flex flex-col gap-2">
            <PageTitle title="New Company" tag="h6" />
            <FormFieldSet
              type="select"
              title="New Company"
              noTitle
              placeholder="Select New Company"
              onChange={(val) => {
                console.log({ val });
                setCurrentModalData((prev) => ({
                  ...prev,
                  selectedPlacement: prev?.employeePlacements?.find(
                    (a) => a.value === val,
                  ),
                }));
              }}
              options={currentModalData?.employeePlacements}
            />
            <div />
            {currentModalData?.selectedPlacement?.value && (
              <div className="flex justify-between items-center gap-2">
                <div className="w-10 h-10 leading-none text-sm aspect-square rounded-full flex items-center justify-center bg-primary/10 text-primary border border-primary">
                  {currentModalData?.selectedPlacement?.metaData
                    ?.clientPlacement?.placement || "-"}
                </div>
                <KeyValue
                  label={
                    currentModalData?.selectedPlacement?.metaData?.jobInfo
                      ?.billClient?.businessName || "-"
                  }
                  value={
                    currentModalData?.selectedPlacement?.metaData?.jobInfo
                      ?.jobTitle || "-"
                  }
                  oneLine={false}
                />
                <KeyValue
                  label={"Bill Rate"}
                  value={formatCurrency(
                    currentModalData?.selectedPlacement?.metaData?.payments[
                      currentModalData?.selectedPlacement?.metaData?.payments
                        ?.length - 1
                    ]?.billRate?.rate || 0,
                  )}
                  oneLine={false}
                />
                <KeyValue
                  label={"Pay Rate"}
                  value={formatCurrency(
                    currentModalData?.selectedPlacement?.metaData?.payments[
                      currentModalData?.selectedPlacement?.metaData?.payments
                        ?.length - 1
                    ]?.payRate?.payRatePerHour || 0,
                  )}
                  oneLine={false}
                />
              </div>
            )}
          </div>
          {currentModalData?.selectedPlacement?.value && (
            <div className="p-3 bg-info-100/40 rounded-md flex flex-col gap-2">
              {`${
                currentModalData?.selectedPlacement?.metaData?.jobInfo
                  ?.billClient?.businessName || "-"
              } will be reassigned from ${
                currentModalData?.selectedPlacement?.metaData?.clientPlacement
                  ?.placement || "-"
              } to ${currentModalData?.currentPlacement?.meta?.id?.clientPlacement?.placement || "-"}`}
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <Button
              title="Cancel"
              variant="outlined"
              onClick={() => {
                setCurrentModalData({
                  currentPlacement: {},
                  selectedPlacement: {},
                  employeePlacements: [],
                });
                setShowModal(false);
              }}
            />
            <Button title="Confirm Switch" onClick={handleConfirmSwitch} />
          </div>
        </div>
      </Modal>
      <Backdrop isOpen={isLoading}>
        <LogoLoader />
      </Backdrop>
    </>
  );
};

export default PlacementAlerts;
